// Hộp "Công cụ" của Bộ soi ảnh: biểu đồ sáng, báo cháy, ống hút màu, lưới căn, xoay / lật để soi.
import { FlipHorizontal, RotateCw } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Histogram } from "./pixels";
import type { Orientation } from "./orientation";
import type { ColorPin, ToolFlags } from "./types";

function Toggle({
  label,
  hint,
  on,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  on: boolean;
  disabled?: string | undefined;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className={cn("flex min-h-9 items-start gap-2 py-1", disabled && "opacity-50")} title={disabled ?? hint}>
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4"
        checked={on && !disabled}
        disabled={!!disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="block font-medium">{label}</span>
        <span className="block text-[11px] text-white/65">{disabled ?? hint}</span>
      </span>
    </label>
  );
}

/** Biểu đồ sáng: vùng xám là độ sáng, ba đường đỏ / lục / lam là ba kênh màu. */
export function HistogramChart({ h }: { h: Histogram }) {
  const W = 256;
  const H = 72;
  let peak = 1;
  for (let i = 1; i < 255; i++) peak = Math.max(peak, h.l[i]!, h.r[i]!, h.g[i]!, h.b[i]!);
  const y = (n: number) => H - Math.min(H, (n / peak) * H);
  const path = (arr: Uint32Array) => Array.from(arr, (n, i) => `${i === 0 ? "M" : "L"}${i},${y(n).toFixed(1)}`).join("");
  const area = `${path(h.l)}L255,${H}L0,${H}Z`;
  const clipHi = h.total ? (h.l[255]! / h.total) * 100 : 0;
  const clipLo = h.total ? (h.l[0]! / h.total) * 100 : 0;
  return (
    <figure className="rounded bg-black/50 p-1">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-[72px] w-full" role="img" aria-label="Biểu đồ sáng của vùng đang xem">
        <path d={area} fill="rgba(255,255,255,0.35)" />
        <path d={path(h.r)} fill="none" stroke="#ff5a5a" strokeWidth="1" />
        <path d={path(h.g)} fill="none" stroke="#5ad15a" strokeWidth="1" />
        <path d={path(h.b)} fill="none" stroke="#5a8cff" strokeWidth="1" />
      </svg>
      <figcaption className="flex justify-between text-[11px] text-white/70">
        <span>Mất đen {clipLo.toFixed(1)}%</span>
        <span>Cháy sáng {clipHi.toFixed(1)}%</span>
      </figcaption>
    </figure>
  );
}

export function ToolsPanel({
  tools,
  onTools,
  blocked,
  hist,
  pins,
  onClearPins,
  orientation,
  onRotate,
  onFlip,
  onClose,
}: {
  tools: ToolFlags;
  onTools: (t: ToolFlags) => void;
  /** Lý do khoá ba công cụ đọc điểm ảnh; undefined = đọc được. */
  blocked?: string | undefined;
  hist: Histogram | null;
  pins: ColorPin[];
  onClearPins: () => void;
  orientation: Orientation;
  onRotate: () => void;
  onFlip: () => void;
  onClose: () => void;
}) {
  const set = (k: keyof ToolFlags) => (v: boolean) => onTools({ ...tools, [k]: v });
  return (
    <div
      role="dialog"
      aria-label="Công cụ soi ảnh"
      className="absolute right-2 top-2 z-30 max-h-[calc(100%-4.5rem)] w-72 max-w-[calc(100%-1rem)] overflow-y-auto rounded-lg bg-black/85 p-3 text-xs text-white shadow-xl"
      onPointerDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      <div className="mb-1 flex items-center justify-between">
        <strong>Công cụ soi</strong>
        <button type="button" className="rounded px-2 py-1 hover:bg-white/15" onClick={onClose} aria-label="Đóng công cụ soi">
          ✕
        </button>
      </div>
      <Toggle
        label="Biểu đồ sáng"
        hint="Độ sáng + ba kênh màu của vùng đang xem"
        on={tools.histogram}
        disabled={blocked}
        onChange={set("histogram")}
      />
      {tools.histogram && !blocked ? (
        hist ? <HistogramChart h={hist} /> : <p className="py-1 text-white/60">Đang đọc ảnh…</p>
      ) : null}
      <Toggle
        label="Báo cháy sáng / mất đen"
        hint="Đỏ: cháy sáng, xanh: mất chi tiết vùng tối"
        on={tools.clipping}
        disabled={blocked}
        onChange={set("clipping")}
      />
      <Toggle
        label="Ống hút màu"
        hint="Di chuột lên ảnh để xem mã màu, bấm để ghim (tối đa 3 điểm)"
        on={tools.eyedropper}
        disabled={blocked}
        onChange={set("eyedropper")}
      />
      {tools.eyedropper && pins.length > 0 ? (
        <div className="mb-1 space-y-1 pl-6">
          {pins.map((p, i) => (
            <div key={`${p.hex}-${i}`} className="flex items-center gap-2 tabular-nums">
              <span className="h-4 w-4 rounded border border-white/40" style={{ background: p.hex }} aria-hidden />
              <span>
                Điểm {i + 1}: {p.hex} · R{p.r} G{p.g} B{p.b}
              </span>
            </div>
          ))}
          <button type="button" className="rounded px-2 py-1 text-white/80 underline hover:bg-white/10" onClick={onClearPins}>
            Bỏ các điểm ghim
          </button>
        </div>
      ) : null}
      <Toggle label="Lưới căn" hint="Chia 3 + đường giữa — kiểm ảnh thẳng, cân" on={tools.grid} onChange={set("grid")} />
      <div className="mt-2 flex gap-2 border-t border-white/15 pt-2">
        <button
          type="button"
          onClick={onRotate}
          className="inline-flex min-h-9 items-center gap-1 rounded px-2 hover:bg-white/15"
          title="Chỉ xoay để soi — không đổi ảnh thật"
        >
          <RotateCw className="h-4 w-4" aria-hidden /> Xoay 90° {orientation.rotate ? `(${orientation.rotate}°)` : ""}
        </button>
        <button
          type="button"
          onClick={onFlip}
          aria-pressed={orientation.flip}
          className={cn("inline-flex min-h-9 items-center gap-1 rounded px-2 hover:bg-white/15", orientation.flip && "bg-white/20")}
          title="Chỉ lật để soi — không đổi ảnh thật"
        >
          <FlipHorizontal className="h-4 w-4" aria-hidden /> Lật ngang
        </button>
      </div>
      <p className="mt-1 text-[11px] text-white/55">Xoay / lật chỉ đổi cách xem trên máy bạn, không đổi ảnh thật.</p>
    </div>
  );
}
